import { useState, useCallback, useMemo, useEffect } from 'react';
import { leaguesAPI } from '../../../../services/apiService';

export const useDraftPick = (socket, draftState, myTeam, leagueId, isCommissioner, draftedPlayerIds, standardContractLength) => {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [makePickError, setMakePickError] = useState(null);
  const [lastPickResult, setLastPickResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [autoPickEnabled, setAutoPickEnabled] = useState(false);

  // Toggle auto-pick
  const toggleAutoPick = useCallback(() => {
    setAutoPickEnabled(prev => !prev);
  }, []);

  // Select player
  const selectPlayer = useCallback((player) => {
    console.log('[useDraftPick] selectPlayer called with:', player?.info?.full_name || player);
    setSelectedPlayer(player);
    setMakePickError(null);
  }, []);

  // Deselect player
  const deselectPlayer = useCallback(() => {
    setSelectedPlayer(null);
    setMakePickError(null);
  }, []);

  // Clear selected player if someone else drafts them
  useEffect(() => {
    if (!selectedPlayer || !draftedPlayerIds || draftedPlayerIds.size === 0) return;
    const lpId = selectedPlayer.ids?.league_player || selectedPlayer.league_player_id;
    if (lpId && draftedPlayerIds.has(lpId)) {
      setSelectedPlayer(null);
      setMakePickError('This player was just drafted by another team');
    }
  }, [draftedPlayerIds, selectedPlayer]);

  // Validation
  const pickValidation = useMemo(() => {
    const errors = [];

    if (!draftState) {
      errors.push('Draft state not loaded');
    } else if (draftState.status !== 'in_progress') {
      errors.push('Draft is not in progress');
    } else if (draftState.paused) {
      errors.push('Draft is paused');
    }

    if (!myTeam) {
      errors.push('Team information not loaded');
    }

    const isMyTurn = draftState?.currentPick?.current_owner_team_id === myTeam?.team_id;
    if (!isMyTurn && !isCommissioner) {
      errors.push('It is not your turn');
    }

    if (!selectedPlayer) {
      errors.push('No player selected');
    } else if (selectedPlayer.league_context?.current_owner_team_id) {
      errors.push('Player is already owned');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }, [draftState, myTeam, selectedPlayer, isCommissioner]);

  const canMakePick = pickValidation.isValid;

  // Make pick
  const makePick = useCallback(async (player, assignment) => {
    // Re-validate at call time (not just memoized value)
    const currentValidation = pickValidation;
    if (!currentValidation.isValid) {
      setMakePickError(currentValidation.errors[0]);
      return;
    }

    if (!socket || !draftState?.currentPick) {
      setMakePickError('Draft state not ready');
      return;
    }

    if (!standardContractLength) {
      setMakePickError('League settings not loaded');
      return;
    }

    setIsProcessing(true);
    setMakePickError(null);

    try {
      // Get league_player_id from player
      const playerId = player.ids?.league_player;

      if (!playerId) {
        throw new Error('Player ID not found');
      }

      // Emit pick to server — include commissioner_override when picking for another team
      const isMyTurn = draftState.currentPick?.current_owner_team_id === myTeam?.team_id;
      socket.emit('make-pick', {
        pick_id: draftState.currentPick.pick_id,
        league_player_id: playerId,
        contract_years: standardContractLength,
        roster_status: assignment?.roster_status || 'active',
        roster_position: assignment?.roster_position || null,
        commissioner_override: isCommissioner && !isMyTurn
      });

      setLastPickResult({
        success: true,
        message: `${player.info?.full_name || 'Player'} drafted!`,
        player
      });

      // Clear selection
      setSelectedPlayer(null);

    } catch (err) {
      setMakePickError(err.message);
      setLastPickResult({
        success: false,
        message: err.message,
        player
      });
    } finally {
      setIsProcessing(false);
    }
  }, [pickValidation, socket, draftState, standardContractLength, myTeam, isCommissioner]);

  // Skip pick (commissioner only)
  const skipPick = useCallback(async () => {
    if (!isCommissioner) {
      setMakePickError('Only commissioners can skip picks');
      return;
    }

    if (!draftState?.currentPick) {
      setMakePickError('No current pick to skip');
      return;
    }

    setIsProcessing(true);
    try {
      socket.emit('skip-pick', {
        league_id: leagueId,
        year: draftState.year,
        pick_id: draftState.currentPick.pick_id
      });
    } catch (err) {
      setMakePickError(err.message);
    } finally {
      setIsProcessing(false);
    }
  }, [isCommissioner, draftState?.currentPick, draftState?.year, socket, leagueId]);

  // Undo pick (commissioner only)
  const undoPick = useCallback(async () => {
    if (!isCommissioner) {
      setMakePickError('Only commissioners can undo picks');
      return;
    }

    setIsProcessing(true);
    try {
      socket.emit('undo-pick', {
        league_id: leagueId,
        year: draftState.year
      });
    } catch (err) {
      setMakePickError(err.message);
    } finally {
      setIsProcessing(false);
    }
  }, [isCommissioner, draftState?.year, socket, leagueId]);

  // Get suggested picks for the current team
  const getSuggestions = useCallback(async () => {
    const teamId = draftState?.currentPick?.current_owner_team_id;
    if (!teamId || !leagueId) return;

    setLoadingSuggestions(true);
    try {
      const result = await leaguesAPI.getSuggestedPick(leagueId, teamId);
      if (result.success) {
        setSuggestions(result.suggestions || []);
      }
    } catch (err) {
      console.error('Error getting suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [draftState?.currentPick?.current_owner_team_id, leagueId]);

  // Request auto-pick for own turn
  const requestAutoPick = useCallback(() => {
    if (!socket || !draftState?.currentPick) return;
    socket.emit('request-auto-pick', {
      league_id: leagueId,
      year: draftState.year
    });
  }, [socket, draftState?.currentPick, draftState?.year, leagueId]);

  // Clear error
  const clearError = useCallback(() => {
    setMakePickError(null);
  }, []);

  return {
    selectedPlayer,
    isProcessing,
    makePickError,
    lastPickResult,
    selectPlayer,
    deselectPlayer,
    makePick,
    skipPick,
    undoPick,
    canMakePick,
    pickValidation,
    clearError,
    suggestions,
    loadingSuggestions,
    getSuggestions,
    autoPickEnabled,
    toggleAutoPick,
    requestAutoPick
  };
};
